/**
 * Magic-link landing page (`/auth/magic?token=…[&redirect=/path]`).
 *
 * Consumes the single-use token via `verifyMagicLink`, then redirects (replacing
 * history so the token never lingers in the back-stack). On failure it shows an
 * error with a path back to request a fresh link. Public route — sibling of
 * `/login`, outside the auth guard.
 */
import { useEffect, useRef, useState, type ReactElement } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useAuth } from '../auth/AuthContext.js';
import { Alert, Button, Spinner } from '../components/ui/index.js';
import { toMessage } from '../lib/useAsync.js';

export default function MagicLinkCallback(): ReactElement {
  const { verifyMagicLink } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [error, setError] = useState<string | null>(null);
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return; // StrictMode double-invoke guard — consume once.
    ran.current = true;

    const token = params.get('token');
    if (!token) {
      setError('This sign-in link is missing its token.');
      return;
    }
    const redirect = params.get('redirect');
    const dest = redirect && redirect.startsWith('/') ? redirect : '/';

    verifyMagicLink(token)
      .then(() => navigate(dest, { replace: true }))
      .catch((err: unknown) => setError(toMessage(err)));
  }, [params, verifyMagicLink, navigate]);

  return (
    <div
      className="grid min-h-screen place-items-center p-6"
      style={{
        background:
          'radial-gradient(120% 90% at 50% -10%, var(--color-primary-soft), var(--color-bg) 60%)',
      }}
    >
      <div className="w-full max-w-[380px]">
        <div className="mb-[30px] flex flex-col items-center gap-4">
          <span
            aria-hidden="true"
            className="grid h-[46px] w-[46px] place-items-center rounded-[14px] bg-primary shadow-[0_0_28px_-4px_var(--hl-primary)]"
          >
            <span className="h-[13px] w-[13px] rounded-full bg-white motion-safe:animate-pulse" />
          </span>
          <div className="text-center">
            <h1 className="text-[17px] font-extrabold tracking-[0.18em] text-text">HOTLINE</h1>
            <p className="text-[13px] text-muted">Operator console</p>
          </div>
        </div>

        <div className="flex flex-col gap-[15px] rounded-xl border border-border bg-surface p-[26px] shadow-lg">
          {error ? (
            <>
              <Alert>{error}</Alert>
              <Button
                type="button"
                variant="primary"
                size="block"
                className="h-10"
                onClick={() => navigate('/login', { replace: true })}
              >
                Request a new link
              </Button>
            </>
          ) : (
            <div className="flex items-center justify-center gap-3 py-2 text-sm text-muted">
              <Spinner size="sm" label="Signing you in…" />
              Signing you in…
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
